import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { AttachmentRejection, attachmentContentType, validateAttachments } from './chatAttachments'
import { AttachmentUploadUrlDTO, ConversationAttachmentDTO, ConversationType } from 'models/conversations'
import { useEffect, useRef, useState } from 'react'

import axios from 'axios'
import { logger } from 'utils/logging'
import { putAttachmentFile } from './attachmentUpload'
import { useRequestAttachmentUploadUrls } from 'dataQueries/conversations.query'

export enum PendingAttachmentStatus {
  PENDING = 'PENDING',
  UPLOADING = 'UPLOADING',
  UPLOADED = 'UPLOADED',
  FAILED = 'FAILED',
}

export const UPLOAD_URL_REUSE_MS = 50 * 60_000

export interface PendingAttachmentTarget extends AttachmentUploadUrlDTO {
  issuedAt: number
}

export interface PendingAttachment {
  key: string
  file: File
  status: PendingAttachmentStatus
  progress: number
  attachmentId?: string
  target?: PendingAttachmentTarget
}

export interface ComposerAttachments {
  items: PendingAttachment[]
  rejections: AttachmentRejection[]
  canSend: boolean
  onRemove: (key: string) => void
  onRetry: (key: string) => void
}

export type AttachmentUploadStep = 'upload_urls' | 'put'

interface UploadOutcome {
  result: 'uploaded' | 'failed' | 'cancelled'
  step?: AttachmentUploadStep
  status?: number
}

export interface AttachmentUploadFailure {
  step: AttachmentUploadStep
  failed: number
  total: number
  status?: number
}

export class AttachmentUploadError extends Error {
  readonly failure: AttachmentUploadFailure

  constructor(failure: AttachmentUploadFailure) {
    super('Chat attachment upload failed')
    this.name = 'AttachmentUploadError'
    this.failure = failure
  }
}

export class AttachmentUploadCancelledError extends Error {
  constructor() {
    super('Chat attachment upload cancelled')
    this.name = 'AttachmentUploadCancelledError'
  }
}

export const toMessageAttachment = (item: PendingAttachment): ConversationAttachmentDTO => ({
  id: item.attachmentId ?? item.key,
  filename: item.file.name,
  contentType: attachmentContentType(item.file),
  sizeBytes: item.file.size,
  url: null,
})

interface Options {
  conversationId?: string
  conversationType?: ConversationType
}

const logContext = (action: string, metadata: Record<string, unknown>) => ({
  context: { component: 'useChatAttachments', action, metadata },
})

export const useChatAttachments = ({ conversationId, conversationType }: Options = {}) => {
  const requestUploadUrls = useRequestAttachmentUploadUrls()
  const [items, setItems] = useState<PendingAttachment[]>([])
  const [rejections, setRejections] = useState<AttachmentRejection[]>([])
  const itemsRef = useRef<PendingAttachment[]>([])
  const controllersRef = useRef(new Map<string, AbortController>())
  const conversationIdRef = useRef(conversationId)
  const lastUploadConversationIdRef = useRef<string | undefined>(undefined)
  const sessionRef = useRef(0)
  const conversationTypeRef = useRef(conversationType)
  conversationTypeRef.current = conversationType

  const commit = (next: PendingAttachment[]) => {
    itemsRef.current = next
    setItems(next)
  }

  const patch = (key: string, changes: Partial<PendingAttachment>) => {
    if (!itemsRef.current.some((item) => item.key === key)) return
    commit(itemsRef.current.map((item) => (item.key === key ? { ...item, ...changes } : item)))
  }

  const abort = (key: string) => {
    controllersRef.current.get(key)?.abort()
    controllersRef.current.delete(key)
  }

  const drop = (keys?: string[]) => {
    const dropped = keys ? new Set(keys) : null
    const isDropped = (item: PendingAttachment) => !dropped || dropped.has(item.key)
    itemsRef.current.filter(isDropped).forEach((item) => abort(item.key))
    commit(itemsRef.current.filter((item) => !isDropped(item)))
  }

  useEffect(() => {
    if (conversationIdRef.current === conversationId) return
    conversationIdRef.current = conversationId
    sessionRef.current += 1
    controllersRef.current.forEach((controller) => controller.abort())
    controllersRef.current.clear()
    itemsRef.current = []
    setItems([])
    setRejections([])
  }, [conversationId])

  useEffect(() => {
    const controllers = controllersRef.current
    const session = sessionRef
    return () => {
      session.current += 1
      controllers.forEach((controller) => controller.abort())
    }
  }, [])

  const reportUploadFailure = (targetConversationId: string, step: AttachmentUploadStep, failed: PendingAttachment[], status?: number, error?: unknown) => {
    logger.warn(
      step === 'upload_urls' ? 'ChatWidget: attachment upload URL request failed' : 'ChatWidget: attachment upload failed',
      error,
      logContext(step === 'upload_urls' ? 'requestUploadUrls' : 'putAttachment', {
        conversationId: targetConversationId,
        step,
        status,
        count: failed.length,
        attachmentIds: failed.map((item) => item.attachmentId).filter(Boolean),
        contentTypes: failed.map((item) => attachmentContentType(item.file)),
        sizeBytes: failed.map((item) => item.file.size),
      })
    )
    logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ATTACHMENT_UPLOAD_FAILED, {
      conversationType: conversationTypeRef.current ?? null,
      step,
      count: failed.length,
    })
  }

  const upload = async (targetConversationId: string, batch: PendingAttachment[]): Promise<UploadOutcome[]> => {
    const controllers = new Map(batch.map((item) => {
      abort(item.key)
      const controller = new AbortController()
      controllersRef.current.set(item.key, controller)
      patch(item.key, { status: PendingAttachmentStatus.UPLOADING, progress: 0, attachmentId: undefined })
      return [item.key, controller] as const
    }))
    const controllerOf = (item: PendingAttachment) => controllers.get(item.key) as AbortController

    const settle = (item: PendingAttachment, step: AttachmentUploadStep, attachmentId?: string, status?: number): UploadOutcome => {
      const controller = controllerOf(item)
      if (controller.signal.aborted) return { result: 'cancelled' }
      if (controllersRef.current.get(item.key) === controller) controllersRef.current.delete(item.key)
      patch(item.key, attachmentId
        ? { status: PendingAttachmentStatus.UPLOADED, progress: 100, attachmentId }
        : { status: PendingAttachmentStatus.FAILED })
      return attachmentId ? { result: 'uploaded' } : { result: 'failed', step, status }
    }

    const startedAt = Date.now()
    const isReusable = (item: PendingAttachment) => !!item.target && startedAt - item.target.issuedAt < UPLOAD_URL_REUSE_MS
    const needingUrl = batch.filter((item) => !isReusable(item))
    const targets = new Map(batch.filter(isReusable).map((item) => [item.key, item.target as PendingAttachmentTarget]))
    const outcomes = new Map<string, UploadOutcome>()

    if (needingUrl.length) {
      try {
        const minted = await requestUploadUrls(targetConversationId, needingUrl.map((item) => ({
          filename: item.file.name,
          contentType: attachmentContentType(item.file),
          sizeBytes: item.file.size,
        })))
        if (!Array.isArray(minted) || minted.length !== needingUrl.length) {
          throw new Error(`Expected ${needingUrl.length} upload URLs, received ${Array.isArray(minted) ? minted.length : 'none'}`)
        }
        const issuedAt = Date.now()
        needingUrl.forEach((item, index) => {
          const target = { ...minted[index], issuedAt }
          targets.set(item.key, target)
          patch(item.key, { target })
        })
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined
        needingUrl.forEach((item) => outcomes.set(item.key, settle(item, 'upload_urls', undefined, status)))
        const failed = needingUrl.filter((item) => outcomes.get(item.key)?.result === 'failed')
        if (failed.length) reportUploadFailure(targetConversationId, 'upload_urls', failed, status, error)
      }
    }

    await Promise.all(batch.map(async (item) => {
      if (outcomes.has(item.key)) return
      const controller = controllerOf(item)
      const target = targets.get(item.key)
      if (!target || controller.signal.aborted) {
        outcomes.set(item.key, { result: 'cancelled' })
        return
      }
      const put = await putAttachmentFile(item.file, target, controller.signal, (progress) => {
        if (controller.signal.aborted) return
        if (itemsRef.current.find((candidate) => candidate.key === item.key)?.progress === progress) return
        patch(item.key, { progress })
      })
      const storedByEarlierAttempt = !put.ok && put.status === 412 && !needingUrl.includes(item)
      if (put.ok || storedByEarlierAttempt) {
        outcomes.set(item.key, settle(item, 'put', target.attachmentId))
        return
      }
      const outcome = settle(item, 'put', undefined, put.status)
      if (outcome.result === 'failed') {
        reportUploadFailure(targetConversationId, 'put', [{ ...item, attachmentId: target.attachmentId }], put.status, put.reason === 'exception' ? put.error : undefined)
      }
      outcomes.set(item.key, outcome)
    }))

    return batch.map((item) => outcomes.get(item.key) ?? { result: 'cancelled' })
  }

  const addFiles = (files: File[]) => {
    const validation = validateAttachments(files, itemsRef.current.length)
    setRejections(validation.rejections)
    const rejectedByReason = new Map<string, number>()
    validation.rejections.forEach(({ reason }) => rejectedByReason.set(reason, (rejectedByReason.get(reason) ?? 0) + 1))
    rejectedByReason.forEach((count, reason) => logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ATTACHMENT_REJECTED, {
      conversationType: conversationTypeRef.current ?? null,
      reason: reason.toLowerCase(),
      count,
    }))
    if (!validation.accepted.length) return
    const added = validation.accepted.map((file) => ({
      key: crypto.randomUUID(),
      file,
      status: PendingAttachmentStatus.PENDING,
      progress: 0,
    }))
    commit([...itemsRef.current, ...added])
    if (conversationIdRef.current) void upload(conversationIdRef.current, added)
  }

  const remove = (key: string) => {
    drop([key])
    setRejections([])
  }

  const retry = (key: string) => {
    const item = itemsRef.current.find((candidate) => candidate.key === key)
    const targetConversationId = conversationIdRef.current ?? lastUploadConversationIdRef.current
    if (!item || item.status !== PendingAttachmentStatus.FAILED || !targetConversationId) return
    void upload(targetConversationId, [item])
  }

  const uploadedIds = () => itemsRef.current
    .filter((item) => item.status === PendingAttachmentStatus.UPLOADED && !!item.attachmentId)
    .map((item) => item.attachmentId as string)

  const captureSession = () => {
    const session = sessionRef.current
    return () => sessionRef.current === session
  }

  const uploadAll = async (targetConversationId: string): Promise<string[]> => {
    const isCurrentSession = captureSession()
    lastUploadConversationIdRef.current = targetConversationId
    const batch = itemsRef.current.filter((item) =>
      item.status === PendingAttachmentStatus.PENDING || item.status === PendingAttachmentStatus.FAILED)
    if (batch.length) {
      const outcomes = await upload(targetConversationId, batch)
      if (!isCurrentSession()) throw new AttachmentUploadCancelledError()
      const failures = outcomes.filter((outcome) => outcome.result === 'failed')
      if (failures.length) {
        throw new AttachmentUploadError({
          step: failures[0].step ?? 'put',
          failed: failures.length,
          total: batch.length,
          status: failures[0].status,
        })
      }
    }
    if (!isCurrentSession()) throw new AttachmentUploadCancelledError()
    return uploadedIds()
  }

  const clear = (keys?: string[]) => {
    if (!keys) sessionRef.current += 1
    drop(keys)
    setRejections([])
  }

  const isUploading = items.some((item) => item.status === PendingAttachmentStatus.UPLOADING)
  const canSend = !isUploading && (!conversationId || items.every((item) => item.status === PendingAttachmentStatus.UPLOADED))

  return {
    items,
    rejections,
    canSend,
    isUploading,
    addFiles,
    onRemove: remove,
    onRetry: retry,
    uploadAll,
    uploadedIds,
    captureSession,
    clear,
  }
}
